import React from 'react';

let _ctlGradId = 0;

// Shooting stars distributed around the entire loop, including the dark left region.
// headPos is the path position (0–1000) where the comet head sits at cycle start.
const STARS = [
  { dur: 16.4, delay: -0.5,  headPos: 50,  visStart: 0.74, visPeak: 0.82, visEnd: 0.92 },
  { dur: 21.7, delay: -3.7,  headPos: 150, visStart: 0.76, visPeak: 0.84, visEnd: 0.93 },
  { dur: 13.9, delay: -2.1,  headPos: 250, visStart: 0.72, visPeak: 0.80, visEnd: 0.90 },
  { dur: 24.6, delay: -8.5,  headPos: 380, visStart: 0.78, visPeak: 0.86, visEnd: 0.94 },
  { dur: 18.2, delay: -5.4,  headPos: 500, visStart: 0.74, visPeak: 0.82, visEnd: 0.92 },
  { dur: 22.8, delay: -10.2, headPos: 620, visStart: 0.80, visPeak: 0.88, visEnd: 0.96 },
  { dur: 19.5, delay: -14.6, headPos: 750, visStart: 0.76, visPeak: 0.84, visEnd: 0.93 },
  { dur: 15.7, delay: -7.9,  headPos: 880, visStart: 0.72, visPeak: 0.81, visEnd: 0.91 },
];

// 8 tiny sparkles inside the ribbon — minimal, almost subliminal.
const SPARKLES = [
  { x: 32,  y: 10,  size: 0.2, dur: 11.3, delay: -0.4 },
  { x: 11,  y: 28,  size: 0.2, dur: 9.6,  delay: -3.2 },
  { x: 32,  y: 50,  size: 0.2, dur: 12.0, delay: -2.7 },
  { x: 50,  y: 26,  size: 0.2, dur: 10.5, delay: -6.3 },
  { x: 87,  y: 10,  size: 0.2, dur: 11.7, delay: -1.9 },
  { x: 109, y: 30,  size: 0.2, dur: 9.0,  delay: -7.4 },
  { x: 87,  y: 50,  size: 0.2, dur: 10.7, delay: -0.6 },
  { x: 70,  y: 34,  size: 0.2, dur: 11.9, delay: -8.2 },
];

function ShootingStar({ star, stroke }) {
  const layers = [
    { dash: 30, color: '#8B2A18', opacityPeak: 0.30, widthMul: 0.16 },
    { dash: 12, color: '#C16038', opacityPeak: 0.6,  widthMul: 0.22 },
    { dash: 4,  color: '#F2A878', opacityPeak: 1.0,  widthMul: 0.30 },
  ];
  const lemniscate = "M 60 30 C 50 0, 5 0, 5 30 C 5 60, 50 60, 60 30 C 70 0, 115 0, 115 30 C 115 60, 70 60, 60 30 Z";

  return (
    <>
      {layers.map((layer, i) => {
        const segStart = star.headPos - layer.dash;
        const fromOffset = -segStart;
        const toOffset = fromOffset - 1000;
        const dashArray = `${layer.dash} ${1000 - layer.dash}`;
        const opacityVals = `0;0;${layer.opacityPeak};${layer.opacityPeak};0`;
        const opacityKT = `0;${star.visStart};${star.visPeak};${star.visEnd};1`;
        return (
          <path
            key={i}
            d={lemniscate}
            stroke={layer.color}
            strokeWidth={stroke * layer.widthMul}
            pathLength="1000"
            strokeDasharray={dashArray}
            strokeLinecap="round"
            fill="none"
            opacity="0"
          >
            <animate
              attributeName="stroke-dashoffset"
              from={fromOffset}
              to={toOffset}
              dur={`${star.dur}s`}
              begin={`${star.delay}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values={opacityVals}
              keyTimes={opacityKT}
              dur={`${star.dur}s`}
              begin={`${star.delay}s`}
              repeatCount="indefinite"
            />
          </path>
        );
      })}
    </>
  );
}

function CrystalSparkle({ x, y, size, dur, delay }) {
  const arm = size * 2.6;
  const lineW = size * 0.32;
  return (
    <g
      style={{
        transformOrigin: `${x}px ${y}px`,
        animation: `ctl-sparkle ${dur}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        opacity: 0,
      }}
    >
      <circle cx={x} cy={y} r={size * 1.8} fill="#B85228" opacity="0.32" />
      <circle cx={x} cy={y} r={size * 0.75} fill="#FFE07A" opacity="0.95" />
      <line
        x1={x} y1={y - arm} x2={x} y2={y + arm}
        stroke="#FFD89A" strokeWidth={lineW} strokeLinecap="round"
      />
      <line
        x1={x - arm} y1={y} x2={x + arm} y2={y}
        stroke="#FFD89A" strokeWidth={lineW} strokeLinecap="round"
      />
    </g>
  );
}

// Softer crossing flash — lower peak, gentle fade, soft amber glow.
function CrossingPulse({ dur, delay }) {
  const x = 60, y = 30;
  const armLong = 5.0;
  const armShort = 3.2;
  const lineW = 0.55;
  const lineWThin = 0.36;
  return (
    <g
      style={{
        transformOrigin: `${x}px ${y}px`,
        animation: `ctl-cross-flash ${dur}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        opacity: 0,
        filter: 'blur(0.4px)',
      }}
    >
      <circle cx={x} cy={y} r="2.6" fill="#B85228" opacity="0.25" />
      <line x1={x} y1={y - armLong} x2={x} y2={y + armLong} stroke="#F2BC85" strokeWidth={lineW} strokeLinecap="round" />
      <line x1={x - armLong} y1={y} x2={x + armLong} y2={y} stroke="#F2BC85" strokeWidth={lineW} strokeLinecap="round" />
      <line x1={x - armShort} y1={y - armShort} x2={x + armShort} y2={y + armShort} stroke="#F2BC85" strokeWidth={lineWThin} strokeLinecap="round" opacity="0.7" />
      <line x1={x - armShort} y1={y + armShort} x2={x + armShort} y2={y - armShort} stroke="#F2BC85" strokeWidth={lineWThin} strokeLinecap="round" opacity="0.7" />
    </g>
  );
}

export default function InfinityMark({
  size = 600,
  stroke = 120,
  animate = true,
  className = '',
  ...rest
}) {
  const id = React.useMemo(() => `ctl-inf-${++_ctlGradId}`, []);
  const colorGradId = `${id}-color`;
  const dimGradId = `${id}-dim`;
  const glossId = `${id}-gloss`;
  const shadowId = `${id}-shadow`;
  const blurId = `${id}-blur`;

  const lemniscate = "M 60 30 C 50 0, 5 0, 5 30 C 5 60, 50 60, 60 30 C 70 0, 115 0, 115 30 C 115 60, 70 60, 60 30 Z";

  return (
    <svg
      width={size}
      height={size * 80 / 130}
      viewBox="-5 -10 130 80"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      overflow="visible"
      aria-label="Connection — infinity"
      role="img"
      className={className}
      {...rest}
    >
      <defs>
        <linearGradient id={colorGradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%"  stopColor="#2E0E05" />
          <stop offset="14%" stopColor="#5C1810" />
          <stop offset="28%" stopColor="#8B2A18" />
          <stop offset="42%" stopColor="#B85228" />
          <stop offset="50%" stopColor="#D87040" />
          <stop offset="58%" stopColor="#B85228" />
          <stop offset="72%" stopColor="#8B2A18" />
          <stop offset="86%" stopColor="#5C1810" />
          <stop offset="100%" stopColor="#2E0E05" />
        </linearGradient>

        <linearGradient id={dimGradId} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%"   stopColor="#E89568" stopOpacity="0.6" />
          <stop offset="22%"  stopColor="#E89568" stopOpacity="0.18" />
          <stop offset="50%"  stopColor="#000000" stopOpacity="0" />
          <stop offset="78%"  stopColor="#140502" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#140502" stopOpacity="0.72" />
        </linearGradient>

        <linearGradient id={glossId} x1="50%" y1="0%" x2="50%" y2="100%">
          <stop offset="0%"  stopColor="#D87A48" stopOpacity="0.85" />
          <stop offset="6%"  stopColor="#D87A48" stopOpacity="0.55" />
          <stop offset="14%" stopColor="#D87A48" stopOpacity="0.18" />
          <stop offset="28%" stopColor="#D87A48" stopOpacity="0" />
          <stop offset="100%" stopColor="#D87A48" stopOpacity="0" />
        </linearGradient>

        <filter id={shadowId} x="-25%" y="-25%" width="150%" height="150%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2.4" />
          <feOffset dx="0" dy="3.0" result="offsetblur" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.55" />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id={blurId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="0.9" />
        </filter>
      </defs>

      {/* Single flat ribbon — copper-red horizontal palette, no shading layers, no drop shadow. */}
      <path
        d={lemniscate}
        stroke={`url(#${colorGradId})`}
        strokeWidth={stroke}
        pathLength="1000"
      />

      {animate && (
        <>
          <g filter={`url(#${blurId})`}>
            {STARS.map((star, i) => (
              <ShootingStar key={`star-${i}`} star={star} stroke={stroke} />
            ))}
          </g>
          {/* Crossing event — observation → insight → action */}
          <CrossingPulse dur={11.5} delay={-1.4} />
          <CrossingPulse dur={17.3} delay={-9.6} />
        </>
      )}
    </svg>
  );
}
